/*
  GDriveRootPickerDialog — admin folder picker for the import-root
  setting. Lets an owner walk their WHOLE Drive to choose the folder
  everyone else will be locked into. Because this is admin-only and
  the goal is to lock down staff, it deliberately does NOT pass a
  scope query param — it browses unrestricted.
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Loader2, Folder, ChevronRight, ChevronLeft, RefreshCw, CheckCircle2, FolderLock,
} from "lucide-react";

export default function GDriveRootPickerDialog({ open, onOpenChange, onPick, scopeLabel }) {
  const [path, setPath] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadFolder = useCallback(async (parentId) => {
    setLoading(true);
    try {
      const params = parentId ? { parent_id: parentId } : {};
      const r = await api.get("/exercise-programs/gdrive/folders", { params });
      setFolders(r.data?.folders || []);
    } catch (e) {
      toast.error("Couldn't load Drive", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setPath([]);
    loadFolder(null);
  }, [open, loadFolder]);

  function openFolder(f) {
    setPath((p) => [...p, { id: f.id, name: f.name }]);
    loadFolder(f.id);
  }

  function goBack() {
    setPath((p) => {
      const next = p.slice(0, -1);
      loadFolder(next[next.length - 1]?.id || null);
      return next;
    });
  }

  const current = path[path.length - 1];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl w-[92vw] max-h-[85vh] flex flex-col"
        data-testid="gdrive-root-picker"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderLock className="h-4 w-4 text-indigo-700" />
            Choose the import root folder
          </DialogTitle>
          <DialogDescription>
            {scopeLabel ? (
              <>
                Staff imports for <span className="font-medium">{scopeLabel}</span>{" "}
                will be locked to the folder you pick here (and its
                sub-folders). They will not be able to see anything above it.
              </>
            ) : (
              "Pick a folder to lock imports to."
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 py-2 border-b border-zinc-200 text-xs text-zinc-600">
          <Button size="sm" variant="ghost" onClick={goBack} disabled={path.length === 0}>
            <ChevronLeft className="h-3.5 w-3.5" /> Back
          </Button>
          <span className="flex items-center gap-1 flex-1 min-w-0 truncate">
            <span className="text-zinc-500">My Drive</span>
            {path.map((p) => (
              <span key={p.id} className="flex items-center gap-1">
                <ChevronRight className="h-3 w-3 text-zinc-400" />
                <span className="truncate">{p.name}</span>
              </span>
            ))}
          </span>
          <Button
            size="sm" variant="ghost"
            onClick={() => loadFolder(current?.id || null)}
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto py-2 space-y-1">
          {loading ? (
            <div className="text-sm text-zinc-500 py-6 text-center">
              <Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Loading…
            </div>
          ) : folders.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center">
              No sub-folders here. Click &quot;Lock to this folder&quot; to pick the current location.
            </div>
          ) : (
            folders.map((f) => (
              <button
                key={f.id}
                onClick={() => openFolder(f)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-100 text-left text-sm"
                data-testid={`gdrive-root-folder-${f.id}`}
              >
                <Folder className="h-4 w-4 text-amber-600" />
                <span className="flex-1 truncate">{f.name}</span>
                <ChevronRight className="h-3.5 w-3.5 text-zinc-400" />
              </button>
            ))
          )}
        </div>

        <DialogFooter className="border-t pt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => current && onPick(current)}
            disabled={!current}
            data-testid="gdrive-root-pick-this-btn"
          >
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
            Lock to this folder{current ? ` — ${current.name}` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
